/**
 * One in-app notice (RP-34) — shared by the bell's panel and the full-screen inbox.
 *
 * Opening it marks it read and goes to the page it is about. An unread notice can also be
 * dismissed in place, without leaving the screen.
 */

import { useRouter } from "@tanstack/react-router";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMarkNotificationRead } from "@/hooks/use-notifications";
import { cn } from "@/lib/utils";
import type { NotificationView } from "@/schemas/notifications";

function ago(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function NotificationItem({
  notification,
  onNavigate,
}: {
  notification: NotificationView;
  /** Called before following the notice's link — the bell closes its panel. */
  onNavigate?: () => void;
}) {
  const router = useRouter();
  const markRead = useMarkNotificationRead();
  const unread = notification.readAt === null;

  return (
    <div className="group flex items-start gap-1 rounded-md hover:bg-muted">
      <button
        type="button"
        onClick={() => {
          if (unread) markRead.mutate(notification.id);
          onNavigate?.();
          router.history.push(notification.linkPath);
        }}
        className="flex min-w-0 flex-1 items-start gap-2.5 px-2.5 py-2 text-left"
      >
        <span
          aria-hidden
          className={cn(
            "mt-1.5 size-2 shrink-0 rounded-full",
            unread ? "bg-primary" : "bg-transparent",
          )}
        />
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              "block text-sm text-foreground",
              unread ? "font-semibold" : "font-normal",
            )}
          >
            {notification.title}
          </span>
          {notification.body !== null && (
            <span className="line-clamp-2 block text-xs text-muted-foreground">
              {notification.body}
            </span>
          )}
          <span className="block text-xs text-muted-foreground">
            {notification.actorName ?? "Someone"} · {ago(notification.createdAt)}
          </span>
        </span>
      </button>
      {unread && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="mt-1.5 mr-1.5 shrink-0 text-muted-foreground hover:text-foreground"
          disabled={markRead.isPending}
          onClick={() => markRead.mutate(notification.id)}
          aria-label={`Dismiss: ${notification.title}`}
          title="Dismiss"
        >
          <X />
        </Button>
      )}
    </div>
  );
}
